const HORIZON_PRODUCT_CARD_STATE_SECTION = 'horizon-product-card-state';

class HorizonProductCardSwatches extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.states = this.parseStates();
    this.addEventListener('change', this.onVariantChange, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.variantRequestController?.abort();
    this.abortController = undefined;
    this.variantRequestController = undefined;
  }

  parseStates() {
    const source = this.querySelector('[data-horizon-product-card-variant-states]');

    if (!source) return {};

    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      console.error('[horizon-product-card] Invalid variant state payload.', error);
      return {};
    }
  }

  onVariantChange = (event) => {
    const input = event.target.closest('[data-horizon-product-card-variant-id]');
    if (!input || !this.contains(input)) return;

    const card = this.closest('[data-horizon-product-card]');
    if (!card) return;

    const fallbackState = this.states[input.dataset.horizonProductCardVariantId];
    const resolution = this.resolveState(card, input, fallbackState);
    card.querySelector('product-form')?.beginCardVariantChange?.(resolution);
  };

  async resolveState(card, input, fallbackState) {
    this.variantRequestController?.abort();
    const requestController = new AbortController();
    this.variantRequestController = requestController;

    card.setAttribute('aria-busy', 'true');
    const submit = card.querySelector('[data-horizon-product-card-purchase] [type="submit"]');
    const coordinatedForm = card.querySelector('product-form');
    const submitWasDisabled = submit instanceof HTMLButtonElement ? submit.disabled : false;
    if (submit instanceof HTMLButtonElement && !coordinatedForm?.beginCardVariantChange) submit.disabled = true;

    try {
      const state = await this.fetchResolvedState(input, requestController.signal);
      if (requestController.signal.aborted || this.variantRequestController !== requestController) return;
      if (!state && !fallbackState) throw new Error('Product-card variant state was unavailable');

      return await this.applyStateInFrame(card, this.withSwatchSelection(state || fallbackState, input), requestController);
    } catch (error) {
      if (requestController.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return;
      console.warn('[horizon-product-card] Server variant resolution failed; using embedded state.', error);
      if (fallbackState) {
        return await this.applyStateInFrame(card, this.withSwatchSelection(fallbackState, input), requestController);
      } else if (submit instanceof HTMLButtonElement) {
        if (coordinatedForm?.updateCardSubmitState) {
          coordinatedForm.updateCardSubmitState(coordinatedForm.cardCanAdd);
        } else {
          submit.disabled = submitWasDisabled;
        }
      }
    } finally {
      if (this.variantRequestController === requestController) {
        this.variantRequestController = undefined;
        card.removeAttribute('aria-busy');
      }
    }
  }

  withSwatchSelection(state, input) {
    return {
      ...state,
      swatchOptionName: input.dataset.horizonProductCardOptionName,
      swatchOptionValue: input.dataset.horizonProductCardOptionValue,
    };
  }

  applyStateInFrame(card, state, requestController) {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        if (
          !requestController.signal.aborted &&
          this.variantRequestController === requestController &&
          card.isConnected
        ) {
          this.applyState(card, state);
          resolve(state);
          return;
        }
        resolve(null);
      });
    });
  }

  async fetchResolvedState(input, signal) {
    const optionValueId = input.dataset.horizonProductCardOptionValueId;
    const productUrl = input.dataset.horizonProductCardConnectedProductUrl || this.dataset.productUrl;
    if (!optionValueId || !productUrl) return null;

    const lockedOptionValueIds = (this.dataset.productCardLockedOptionValueIds || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    const optionValueIds = [...new Set([...lockedOptionValueIds, optionValueId])];

    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.hash = '';
    requestUrl.search = '';
    requestUrl.searchParams.set('option_values', optionValueIds.join(','));
    requestUrl.searchParams.set('section_id', HORIZON_PRODUCT_CARD_STATE_SECTION);

    const response = await fetch(requestUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal,
    });
    if (!response.ok) throw new Error(`Product-card variant request failed: ${response.status}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = html.querySelector('[data-horizon-product-card-resolved-state]');
    if (!source?.textContent) throw new Error('Product-card variant response was incomplete');

    return JSON.parse(source.textContent);
  }

  applyState(card, state) {
    card.dataset.variantId = String(state.id);
    if (state.productId) card.dataset.productId = String(state.productId);

    const media = card.querySelector('.horizon-product-card__media');
    if (media) {
      media.href = state.url;
      media.innerHTML = state.mediaHtml;
    }

    const titleLink = card.querySelector('.horizon-product-card__title a');
    if (titleLink) titleLink.href = state.url;

    const swatchOptionName = card.querySelector('[data-horizon-product-card-swatch-option-name]');
    if (swatchOptionName && state.swatchOptionName) {
      swatchOptionName.textContent = `${state.swatchOptionName}:`;
    }

    const swatchOptionValue = card.querySelector('[data-horizon-product-card-swatch-option-value]');
    if (swatchOptionValue && state.swatchOptionValue) {
      swatchOptionValue.textContent = state.swatchOptionValue;
    }

    const priceRegion = card.querySelector('.horizon-product-card__price-block');
    if (priceRegion) {
      priceRegion.innerHTML = card.dataset.productCardMode === 'variant'
        ? state.variantCardPriceHtml
        : state.priceHtml;
    }

    const sale = card.querySelector('[data-horizon-product-card-sale]');
    if (sale) {
      const onSale = card.dataset.productCardMode === 'variant'
        ? state.variantCardOnSale
        : state.onSale;
      sale.hidden = sale.dataset.saleEnabled === 'false' || onSale !== true;
      const label = card.querySelector('[data-horizon-product-card-sale-label]');
      const saleLabel = card.dataset.productCardMode === 'variant'
        ? state.variantCardSaleLabel
        : state.saleLabel;
      if (label && typeof saleLabel === 'string') label.textContent = saleLabel;
    }

    const availability = card.querySelector('[data-horizon-product-card-availability]');
    if (availability) {
      const available = card.dataset.productCardMode === 'variant'
        ? state.available
        : (state.productAvailable ?? state.available);
      availability.hidden = available;
      availability.textContent = available ? '' : state.availabilityLabel;
    }

    const volumeRegion = card.querySelector('[data-horizon-product-card-volume]');
    if (volumeRegion) {
      let volumeHtml = card.dataset.productCardMode === 'variant'
        ? state.variantCardVolumeHtml
        : state.volumeHtml;
      const summaryOnly = volumeRegion.dataset?.showVolumePricingDetails === 'false';
      if (summaryOnly) {
        volumeHtml = card.dataset.productCardMode === 'variant'
          ? state.variantCardVolumeSummaryHtml
          : state.volumeSummaryHtml;
      }
      if (summaryOnly && priceRegion && card.dataset.cardPresentation !== 'europe') {
        priceRegion.innerHTML = (card.dataset.productCardMode === 'variant'
          ? state.variantCardCompactPriceHtml
          : state.compactPriceHtml) + volumeHtml;
      }
      volumeRegion.innerHTML = summaryOnly && card.dataset.cardPresentation !== 'europe' ? '' : volumeHtml;
      volumeRegion.hidden = (summaryOnly && card.dataset.cardPresentation !== 'europe') || volumeHtml.length === 0;
    }

    if (card.dataset.cardPresentation === 'europe' && priceRegion) {
      const comparison = priceRegion.querySelector('.horizon-product-card-price__compare');
      if (comparison && card.dataset.compareAtLabel) {
        const label = document.createElement('span');
        label.className = 'horizon-product-card-price__compare-label';
        label.textContent = card.dataset.compareAtLabel;
        comparison.after(label);
      }
    }

    this.updatePurchase(card, state);
  }

  updatePurchase(card, state) {
    const purchase = card.querySelector('[data-horizon-product-card-purchase]');
    if (!purchase) return;

    const canDirectAdd =
      state.canDirectAdd ||
      (card.dataset.productCardMode === 'variant' && state.canVariantCardDirectAdd);

    const quantityRow = purchase.querySelector('[data-horizon-product-card-quantity]');
    const showVolumePricingDetails = card.querySelector('[data-horizon-product-card-volume]')?.dataset.showVolumePricingDetails !== 'false';
    if (quantityRow) quantityRow.hidden = !showVolumePricingDetails || !canDirectAdd || !state.hasVolumePricing;

    const variantInput = purchase.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = state.id;
      variantInput.dataset.horizonProductCardFormVariantId = state.id;
      variantInput.disabled = !canDirectAdd || !state.canAddToCart;
    }

    const quantityInput = purchase.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.value = state.quantity.min;
      quantityInput.min = state.quantity.min;
      quantityInput.dataset.min = state.quantity.min;
      quantityInput.dataset.max = state.quantity.max ?? '';
      quantityInput.step = state.quantity.increment;
      quantityInput.dataset.cartQuantity = state.quantity.cart;

      if (state.quantity.max == null) quantityInput.removeAttribute('max');
      else quantityInput.max = state.quantity.max;
    }

    const submit = purchase.querySelector('[type="submit"]');
    const productForm = purchase.querySelector('product-form');
    if (submit) {
      submit.hidden = !canDirectAdd;
      submit.disabled = !canDirectAdd || !state.canAddToCart;
      if (!productForm?.cardAddInProgress) submit.removeAttribute('aria-disabled');
      productForm?.updateCardSubmitState?.(canDirectAdd && state.canAddToCart);
      const submitLabel = submit.querySelector('[data-horizon-product-card-submit-label]');
      if (submitLabel) submitLabel.textContent = state.submitLabel;
    }

    const chooseOptions = purchase.querySelector('[data-horizon-product-card-choose-options]');
    if (chooseOptions) {
      chooseOptions.hidden = canDirectAdd;
      chooseOptions.href = state.url;
    }

    const learnMore = card.querySelector('[data-horizon-product-card-learn-more]');
    if (learnMore) {
      learnMore.hidden = !canDirectAdd;
      learnMore.href = state.url;
    }

    const error = purchase.querySelector('.product-form__error-message-wrapper');
    if (error) error.hidden = true;

    const quantityElement = purchase.querySelector('horizon-product-card-quantity');
    quantityElement?.updateConstraints?.();
  }
}

if (!customElements.get('horizon-product-card-swatches')) {
  customElements.define('horizon-product-card-swatches', HorizonProductCardSwatches);
}
